import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Car, User, Plus, X, MapPin } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { ToastModal } from '../../components/ui/Toast';
import { formatCurrency, formatVehicleName } from '../../lib/utils';
import { carAPI, bookingAPI } from '../../services/api';
import AddressAutocomplete from '../../components/maps/AddressAutocomplete';

const CreateBooking = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [formData, setFormData] = useState({
    carId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startDate: '',
    endDate: '',
    rentalType: 'daily',
    estimatedKm: '',
    hours: '',
    pickupLocation: '',
    dropoffLocation: '',
    stops: [],
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });

  useEffect(() => {
    fetchAvailableCars();
  }, []);

  useEffect(() => {
    calculateTotalCost();
  }, [formData.carId, formData.startDate, formData.endDate, formData.rentalType, formData.estimatedKm, formData.hours, cars]);

  const fetchAvailableCars = async () => {
    try {
      const response = await carAPI.getAllCars();
      const carsData = response.data.cars || response.data || [];
      const availableCars = carsData.filter(car => car.isAvailable !== false && car.status !== 'inactive');
      setCars(availableCars);
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const calculateTotalCost = () => {
    if (!formData.carId) return;
    const car = cars.find(c => c._id === formData.carId);
    if (!car) return;
    setSelectedCar(car);

    let cost = 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    const km = parseFloat(formData.estimatedKm) || 0;
    const hrs = parseFloat(formData.hours) || 0;

    switch (formData.rentalType) {
      case 'daily':
      case 'rental':
        cost = days * (car.pricePerDay || 0);
        break;
      case 'km-based':
      case 'per-km':
        cost = km * (car.pricePerKm || 0);
        break;
      case 'outstation':
        cost = km * (car.outstationPricePerKm || car.pricePerKm || 0);
        if (car.driverBataPerDay) cost += days * car.driverBataPerDay;
        break;
      case 'local':
        cost = hrs * (car.pricePerHour || 0);
        break;
      default:
        cost = days * (car.pricePerDay || 0);
    }
    setTotalCost(Math.round(cost));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Multi-stop helpers — stops are intermediate destinations between pickup and drop-off
  const addStop = () => {
    setFormData(prev => ({ ...prev, stops: [...prev.stops, ''] }));
  };
  const updateStop = (index, value) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.map((s, i) => (i === index ? value : s))
    }));
  };
  const removeStop = (index) => {
    setFormData(prev => ({ ...prev, stops: prev.stops.filter((_, i) => i !== index) }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.carId) newErrors.carId = 'Please select a car';
    if (!formData.customerName) newErrors.customerName = 'Customer name is required';
    if (!formData.customerEmail) newErrors.customerEmail = 'Email is required';
    if (!formData.customerPhone) newErrors.customerPhone = 'Phone is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.pickupLocation) newErrors.pickupLocation = 'Pickup location is required';

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.startDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);
      await bookingAPI.createBooking({
        ...formData,
        stops: (formData.stops || []).map(s => (s || '').trim()).filter(Boolean),
        totalCost,
        status: 'pending'
      });
      setToast({
        isOpen: true,
        title: 'Booking Created!',
        message: 'The booking has been created successfully.',
        type: 'success'
      });
      setTimeout(() => navigate('/bookings'), 1500);
    } catch (error) {
      console.error('Error creating booking:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Failed to create booking. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Booking"
        description="Create a new vehicle booking"
        action={
          <Button variant="outline" onClick={() => navigate('/bookings')}>
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Bookings
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Car className="h-5 w-5 mr-2" />
                  Car Selection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  label="Select Car"
                  name="carId"
                  value={formData.carId}
                  onChange={handleChange}
                  error={errors.carId}
                  required
                >
                  <option value="">Choose a car...</option>
                  {cars.map(car => (
                    <option key={car._id} value={car._id}>
                      {formatVehicleName(car, { showReg: true })} ({formatCurrency(car.pricePerDay)}/day)
                    </option>
                  ))}
                </Select>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    error={errors.customerName}
                    required
                    placeholder="John Doe"
                  />
                  <Input
                    label="Email"
                    name="customerEmail"
                    type="email"
                    value={formData.customerEmail}
                    onChange={handleChange}
                    error={errors.customerEmail}
                    required
                    placeholder="john@example.com"
                  />
                  <Input
                    label="Phone"
                    name="customerPhone"
                    type="tel"
                    value={formData.customerPhone}
                    onChange={handleChange}
                    error={errors.customerPhone}
                    required
                    placeholder="+91 9876543210"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Booking Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Start Date & Time"
                    name="startDate"
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={handleChange}
                    error={errors.startDate}
                    required
                  />
                  <Input
                    label="End Date & Time"
                    name="endDate"
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={handleChange}
                    error={errors.endDate}
                    required
                  />
                  {/* Rental Type */}
                  <Select
                    label="Rental Type"
                    name="rentalType"
                    value={formData.rentalType}
                    onChange={handleChange}
                    required
                  >
                    <option value="daily">Daily Rental</option>
                    <option value="rental">Multi-Day Rental</option>
                    <option value="km-based">Pay Per KM</option>
                    <option value="outstation">Outstation</option>
                    <option value="local">Local (Hourly)</option>
                    <option value="sale">Sale</option>
                  </Select>

                  {/* Estimated KM — show for km-based / outstation */}
                  {(formData.rentalType === 'km-based' || formData.rentalType === 'outstation') && (
                    <Input
                      label="Estimated KM"
                      name="estimatedKm"
                      type="number"
                      value={formData.estimatedKm}
                      onChange={handleChange}
                      placeholder="e.g. 150"
                    />
                  )}

                  {/* Hours — show for local */}
                  {formData.rentalType === 'local' && (
                    <Input
                      label="Hours"
                      name="hours"
                      type="number"
                      value={formData.hours}
                      onChange={handleChange}
                      placeholder="e.g. 4"
                    />
                  )}

                  <AddressAutocomplete
                    label="Pickup Location"
                    value={formData.pickupLocation}
                    onChange={(loc) => setFormData(prev => ({ ...prev, pickupLocation: loc.address }))}
                    required
                    placeholder="Search pickup address..."
                  />

                  {/* Intermediate stops — for multi-stop trips e.g. Gadag → Koppal → Dharwad */}
                  <div className="md:col-span-2 space-y-2">
                    {formData.stops.map((stop, index) => (
                      <div key={index} className="flex items-end gap-2">
                        <div className="flex-1">
                          <AddressAutocomplete
                            label={`Stop ${index + 1}`}
                            value={stop}
                            onChange={(loc) => updateStop(index, loc.address)}
                            placeholder="Search stop address..."
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          className="mb-1 p-2 text-gray-400 hover:text-red-600"
                          title="Remove stop"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addStop}
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                      Add stop
                    </button>
                  </div>

                  <AddressAutocomplete
                    label="Drop-off Location"
                    value={formData.dropoffLocation}
                    onChange={(loc) => setFormData(prev => ({ ...prev, dropoffLocation: loc.address }))}
                    placeholder="Search drop-off address (optional)"
                  />

                  {/* Notes */}
                  <div className="md:col-span-2">
                    <Input
                      label="Notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any special instructions (optional)"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/bookings')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Booking'}
              </Button>
            </div>
          </form>
        </div>

        {/* Booking Summary */}
        <div>
          <Card className="sticky top-20">
            <CardHeader>
              <CardTitle>Booking Summary</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCar ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-lg">
                      {formatVehicleName(selectedCar, { showYear: false })}
                    </p>
                    <p className="text-sm text-gray-600">{selectedCar.registrationNumber}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rental Type:</span>
                      <span className="font-medium capitalize">{(formData.rentalType || 'daily').replace('-', ' ')}</span>
                    </div>
                    {(formData.rentalType === 'daily' || formData.rentalType === 'rental') && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate:</span>
                          <span className="font-medium">{formatCurrency(selectedCar.pricePerDay)}/day</span>
                        </div>
                        {formData.startDate && formData.endDate && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Days:</span>
                            <span className="font-medium">{Math.max(1, Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)))}</span>
                          </div>
                        )}
                      </>
                    )}
                    {(formData.rentalType === 'km-based' || formData.rentalType === 'outstation') && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate:</span>
                          <span className="font-medium">{formatCurrency(selectedCar.pricePerKm || selectedCar.outstationPricePerKm || 0)}/km</span>
                        </div>
                        {formData.estimatedKm && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Est. KM:</span>
                            <span className="font-medium">{formData.estimatedKm} km</span>
                          </div>
                        )}
                      </>
                    )}
                    {formData.rentalType === 'local' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rate:</span>
                          <span className="font-medium">{formatCurrency(selectedCar.pricePerHour || 0)}/hr</span>
                        </div>
                        {formData.hours && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Hours:</span>
                            <span className="font-medium">{formData.hours} hrs</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total Cost:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatCurrency(totalCost)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Select a car to see booking summary
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ToastModal
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default CreateBooking;
